import { Controller } from "@hotwired/stimulus"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import Underline from "@tiptap/extension-underline"
import TextAlign from "@tiptap/extension-text-align"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import Mention from "@tiptap/extension-mention"

export default class extends Controller {
  static targets = ["input", "editor", "toolbar", "mergeTagList"]
  static values = {
    placeholder: { type: String, default: "Start writing..." },
    mergeTags: { type: Array, default: [] }
  }

  connect() {
    const extensions = [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] }
      }),
      Underline,
      TextAlign.configure({
        types: ["heading", "paragraph"]
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: "text-teal-600 underline dark:text-teal-400" }
      }),
      Placeholder.configure({
        placeholder: this.placeholderValue
      })
    ]

    // Add merge tag support if tags are provided
    if (this.mergeTagsValue.length > 0) {
      extensions.push(
        Mention.configure({
          HTMLAttributes: {
            class: "merge-tag"
          },
          renderText({ node }) {
            return `{{ ${node.attrs.id} }}`
          },
          renderHTML({ node, HTMLAttributes }) {
            return ["span", { ...HTMLAttributes, "data-type": "mention", "data-id": node.attrs.id }, `{{ ${node.attrs.id} }}`]
          }
        })
      )
    }

    this.editor = new Editor({
      element: this.editorTarget,
      extensions,
      content: this.inputTarget.value || "",
      editorProps: {
        attributes: {
          class: "prose prose-sm dark:prose-invert max-w-none min-h-[400px] outline-none focus:outline-none"
        }
      },
      onUpdate: ({ editor }) => {
        this.inputTarget.value = editor.getHTML()
      },
      onSelectionUpdate: () => {
        this.updateToolbarState()
      },
      onTransaction: () => {
        this.updateToolbarState()
      }
    })

    // Sync initial content to hidden field if editor has content
    if (this.editor.getHTML() !== "<p></p>") {
      this.inputTarget.value = this.editor.getHTML()
    }
  }

  disconnect() {
    if (this.editor) {
      this.editor.destroy()
    }
    this.closeMergeTagDropdown()
  }

  // Toolbar actions
  bold() {
    this.editor.chain().focus().toggleBold().run()
  }

  italic() {
    this.editor.chain().focus().toggleItalic().run()
  }

  underline() {
    this.editor.chain().focus().toggleUnderline().run()
  }

  strike() {
    this.editor.chain().focus().toggleStrike().run()
  }

  heading(event) {
    const level = parseInt(event.currentTarget.dataset.level)
    this.editor.chain().focus().toggleHeading({ level }).run()
  }

  bulletList() {
    this.editor.chain().focus().toggleBulletList().run()
  }

  orderedList() {
    this.editor.chain().focus().toggleOrderedList().run()
  }

  blockquote() {
    this.editor.chain().focus().toggleBlockquote().run()
  }

  horizontalRule() {
    this.editor.chain().focus().setHorizontalRule().run()
  }

  alignLeft() {
    this.editor.chain().focus().setTextAlign("left").run()
  }

  alignCenter() {
    this.editor.chain().focus().setTextAlign("center").run()
  }

  alignRight() {
    this.editor.chain().focus().setTextAlign("right").run()
  }

  link() {
    if (this.editor.isActive("link")) {
      this.editor.chain().focus().unsetLink().run()
      return
    }

    const url = prompt("Enter URL:")
    if (url) {
      this.editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run()
    }
  }

  undo() {
    this.editor.chain().focus().undo().run()
  }

  redo() {
    this.editor.chain().focus().redo().run()
  }

  // Merge tag dropdown
  toggleMergeTagDropdown() {
    if (this.hasMergeTagListTarget && this.mergeTagListTarget.classList.contains("hidden")) {
      this.mergeTagListTarget.classList.remove("hidden")
    } else if (this.hasMergeTagListTarget) {
      this.mergeTagListTarget.classList.add("hidden")
    }
  }

  insertMergeTag(event) {
    const tagId = event.currentTarget.dataset.tagId
    this.editor
      .chain()
      .focus()
      .insertContent({
        type: "mention",
        attrs: { id: tagId, label: tagId }
      })
      .insertContent(" ")
      .run()
    this.closeMergeTagDropdown()
  }

  closeMergeTagDropdown() {
    if (this.hasMergeTagListTarget) {
      this.mergeTagListTarget.classList.add("hidden")
    }
  }

  // Update active state classes on toolbar buttons
  updateToolbarState() {
    if (!this.hasToolbarTarget) return

    const buttons = this.toolbarTarget.querySelectorAll("[data-format]")
    buttons.forEach((button) => {
      const format = button.dataset.format
      const level = button.dataset.level
      let isActive = false

      if (format === "heading" && level) {
        isActive = this.editor.isActive("heading", { level: parseInt(level) })
      } else if (format === "align") {
        isActive = this.editor.isActive({ textAlign: button.dataset.align })
      } else {
        isActive = this.editor.isActive(format)
      }

      button.classList.toggle("bg-neutral-200", isActive)
      button.classList.toggle("dark:bg-neutral-600", isActive)
      button.classList.toggle("text-teal-700", isActive)
      button.classList.toggle("dark:text-teal-300", isActive)
    })
  }
}
